import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { buildBreadcrumbList, buildServiceSchema } from '../lib/structuredData';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowRightIcon, MailIcon, ShieldIcon, CheckCircleIcon, SparklesIcon, UsersIcon, SendIcon, LockIcon, BotIcon, StarIcon, CrownIcon, BriefcaseIcon, FileTextIcon, ReceiptIcon, TrendingUpIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type PackageId = 'starter' | 'standard' | 'premium' | 'google';
type EmailPackage = {
  id: PackageId;
  name: string;
  shortName: string;
  tagline: string;
  price: string;
  unit: string;
  storage: string;
  description: string;
  features: string[];
  hasKodee: boolean;
  badge?: string;
  icon: React.ReactNode;
};
const emailPackages: EmailPackage[] = [{
  id: 'starter',
  name: 'Starter Business Email',
  shortName: 'Starter',
  tagline: 'Voor zzp en freelancers die professioneel willen mailen',
  price: '€3,99',
  unit: 'per mailbox per maand',
  storage: '5 GB',
  description: 'Een professioneel mailadres op eigen domein, met Kodee AI inbegrepen. Geschikt als startpunt voor zelfstandige adviseurs en kleine kantoren.',
  features: ['5 GB opslag per mailbox', '5 doorstuurregels en 5 aliassen', 'Werkt met Outlook, Apple Mail, Gmail-app en elke andere mailapp', 'Geavanceerde bescherming tegen spam, malware en phishing', 'SPF, DKIM, DMARC en ARC standaard ingesteld bij oplevering', 'Versleutelde opslag en geen Hostinger-handtekening', 'Gratis domein bij eerste jaar'],
  hasKodee: true,
  icon: <MailIcon className="h-5 w-5" data-id="element-1307" data-mp="3jqqbeiyv20y7" />
}, {
  id: 'standard',
  name: 'Standard Business Email',
  shortName: 'Standard',
  tagline: 'Voor kleine teams die meer ruimte en meer aliassen willen',
  price: '€4,99',
  unit: 'per mailbox per maand',
  storage: '20 GB',
  description: 'De middelste optie. Ruim genoeg voor adviseurs die regelmatig bijlagen versturen en meerdere aliassen willen voor verschillende rollen of diensten.',
  features: ['20 GB opslag per mailbox', '20 doorstuurregels en 10 aliassen', 'Werkt met Outlook, Apple Mail, Gmail-app en elke andere mailapp', 'Geavanceerde bescherming tegen spam, malware en phishing', 'SPF, DKIM, DMARC en ARC standaard ingesteld bij oplevering', 'Versleutelde opslag en geen Hostinger-handtekening', 'Gratis domein bij eerste jaar'],
  hasKodee: true,
  badge: 'Aanrader',
  icon: <SparklesIcon className="h-5 w-5" data-id="element-1308" data-mp="1h6e6jdmqye17" />
}, {
  id: 'premium',
  name: 'Premium Business Email',
  shortName: 'Premium',
  tagline: 'Voor groeiende teams die maximale ruimte en aliassen nodig hebben',
  price: '€6,99',
  unit: 'per mailbox per maand',
  storage: '50 GB',
  description: 'Voor kantoren waar veel mail binnenkomt en uitgaat. Maximale opslag, maximale aliassen en alle AI-tools.',
  features: ['50 GB opslag per mailbox', '50 doorstuurregels en 30 aliassen', 'Werkt met Outlook, Apple Mail, Gmail-app en elke andere mailapp', 'Geavanceerde bescherming tegen spam, malware en phishing', 'SPF, DKIM, DMARC en ARC standaard ingesteld bij oplevering', 'Versleutelde opslag en geen Hostinger-handtekening', 'Gratis domein bij eerste jaar'],
  hasKodee: true,
  icon: <CrownIcon className="h-5 w-5" data-id="element-1309" data-mp="17hgy63lbfixy" />
}, {
  id: 'google',
  name: 'Google Workspace Starter',
  shortName: 'Google Workspace',
  tagline: 'Voor teams die volledig in het Google-ecosysteem werken',
  price: '€10,99',
  unit: 'per gebruiker per maand',
  storage: '30 GB',
  description: 'E-mail op eigen domein, maar dan in de bekende Google-omgeving. Inclusief Gmail, Drive, Agenda, Meet, Docs, Sheets en Slides.',
  features: ['30 GB opslag per gebruiker', 'Gmail op eigen domein met geavanceerde phishing- en malware-bescherming', 'Google Agenda, Drive, Meet, Docs, Sheets en Slides', 'Chat-teamberichten en AppSheet', 'Videoconferenties tot 100 deelnemers', 'Centraal beheer via admin console', 'Wij regelen de DNS, domeinverificatie en oplevering'],
  hasKodee: false,
  icon: <UsersIcon className="h-5 w-5" data-id="element-1310" data-mp="2bsd5p72dgt9" />
}];
type ReachTier = {
  id: string;
  range: string;
  contacts: string;
  emails: string;
  aiCredits: string;
  price: string;
};
const reachTiers: ReachTier[] = [{
  id: 'tier-1',
  range: '0 tot 500 contacten',
  contacts: '500 contacten per maand',
  emails: '3.500 e-mails per maand',
  aiCredits: '10 AI-credits per maand',
  price: '€6,99'
}, {
  id: 'tier-2',
  range: '501 tot 1.000 contacten',
  contacts: '1.000 contacten per maand',
  emails: '7.000 e-mails per maand',
  aiCredits: '20 AI-credits per maand',
  price: '€12,99'
}, {
  id: 'tier-3',
  range: '1.001 tot 2.500 contacten',
  contacts: '2.500 contacten per maand',
  emails: '17.500 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€27,99'
}, {
  id: 'tier-4',
  range: '2.501 tot 5.000 contacten',
  contacts: '5.000 contacten per maand',
  emails: '35.000 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€50,99'
}, {
  id: 'tier-5',
  range: '5.001 tot 10.000 contacten',
  contacts: '10.000 contacten per maand',
  emails: '70.000 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€80,99'
}, {
  id: 'tier-6',
  range: '10.001 tot 50.000 contacten',
  contacts: '50.000 contacten per maand',
  emails: '350.000 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€270,99'
}];
export const EmailPage: React.FC = () => {
  const [selectedPackage, setSelectedPackage] = useState<PackageId>('standard');
  const [selectedReach, setSelectedReach] = useState<string>('tier-1');
  const activePackage = emailPackages.find(p => p.id === selectedPackage) ?? emailPackages[1];
  const activeReach = reachTiers.find(t => t.id === selectedReach) ?? reachTiers[0];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-1311" data-mp="3n9xjkw8txs26">
      <SEO title="Zakelijke e-mail hosting met pakket-keuze en Kodee AI | Noord Digital" description="Vergelijk Starter, Standard, Premium Business Email en Google Workspace. Inclusief Kodee AI, agentic mail en email marketing via Hostinger Reach. Vaste maandprijs, jaarlijks vastgelegd." keywords="zakelijke e-mail hosting, business email starter, business email standard, business email premium, kodee ai, google workspace, eigen domein e-mail, email marketing, hostinger reach" canonical="https://noorddigital.nl/email" jsonLd={[buildServiceSchema({
      name: 'E-mail',
      description: 'Zakelijke e-mail op je eigen domein, netjes ingericht en beveiligd tegen spam en phishing, met keuze uit meerdere pakketten.',
      path: '/email',
      serviceType: 'Zakelijke e-mailhosting'
    }), buildBreadcrumbList([{
      name: 'E-mail',
      path: '/email'
    }])]} data-id="element-1312" data-mp="3rahj26gvl7rh" />
      <Header data-id="element-1313" data-mp="28b019v69b3iu" />

      {/* Hero */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-16 md:py-20" data-id="element-1314" data-mp="28c1p708dfkhz">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1315" data-mp="23coslnrp6z27">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-id="element-1316" data-mp="2i0jmv8lctbhe">
            <div className="lg:col-span-7" data-id="element-1317" data-mp="2i2mhk5xgbssg">
              <h1 className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-1318" data-mp="2d91u37thih7i">
                Zakelijke e-mail met de pakket-keuze die bij je werk past
              </h1>
              <p className="text-white text-opacity-90 mb-8 text-lg leading-relaxed" data-id="element-1319" data-mp="1kke5p3dfvbpq">
                Vier pakketten, allemaal met e-mail op je eigen domein. Bij elk Business-pakket krijg je Kodee AI inbegrepen, de assistent die helpt met opstellen, samenvatten en doorzoeken. Of kies Google Workspace als je liever in de Google-omgeving werkt. Wij regelen de DNS, beveiliging en oplevering, jij krijgt een werkende mailbox op de afgesproken dag.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-1320" data-mp="1i0yq9f4ss1ck">
                <a href="#email-pakketten" className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg" data-id="element-1321" data-mp="1i0fg4kle2h93">
                  Vergelijk de pakketten
                  <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-1322" data-mp="1wpv1fl25huhe" />
                </a>
                <a href="#mailbox-advies" className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg" data-id="element-1323" data-mp="1wrpeyeb3ekbg">
                  Ons mailbox-advies
                  <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-1324" data-mp="3q9mycpdkza4m" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5" data-id="element-1325" data-mp="3m5fa0tb1bw6x">
              <div className="bg-white rounded-2xl p-8 shadow-2xl" data-id="element-1326" data-mp="3h7vs9jf4p1a8">
                <div className="text-center mb-6" data-id="element-1327" data-mp="3h85kldnnbkj3">
                  <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full mb-4 p-3" data-id="element-1328" data-mp="3c9m0lism4itd">
                    <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte, zakelijke e-mail hosting" className="w-full h-full object-cover rounded-full" data-id="element-1329" data-mp="dt9q5mhg3n8t" />
                  </div>
                  <h3 className="font-bold text-[#111827] text-2xl mb-2" data-id="element-1330" data-mp="wi6i3jpde3nv">
                    Norden bewaakt jouw mailverkeer
                  </h3>
                  <p className="text-[#6B7280]" data-id="element-1331" data-mp="rpn4wnulhajl">
                    Aflevergarantie, anti-phishing en geen rare provider-branding onderaan je mail
                  </p>
                </div>
                <div className="space-y-3 pt-6 border-t border-gray-200" data-id="element-1332" data-mp="3e6fd020tumyo">
                  <div className="flex items-center gap-3" data-id="element-1333" data-mp="3t2eshxq9npdu">
                    <ShieldIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1334" data-mp="3o31xgpp8rc06" />
                    <span className="text-sm text-[#6B7280]" data-id="element-1335" data-mp="3o54z66zratkq">
                      Geavanceerde beveiliging (SPF, DKIM, DMARC, ARC)
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1336" data-mp="xt1hr0txhiq">
                    <BotIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1337" data-mp="fni841roydqe" />
                    <span className="text-sm text-[#6B7280]" data-id="element-1338" data-mp="aofdlb9c7pwo">
                      Kodee AI inbegrepen bij alle Business-pakketten
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1339" data-mp="olkekwu71yl2">
                    <LockIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1340" data-mp="knmo7qoot6g3" />
                    <span className="text-sm text-[#6B7280]" data-id="element-1341" data-mp="13adolzguo90p">
                      Spamfilter, virusbescherming en aflevergarantie
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pakket-keuze met toggle */}
      <div id="email-pakketten" className="w-full bg-white py-20 md:py-24" data-id="element-1342" data-mp="z7pezpkbay2o">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1343" data-mp="kjw06731vzl5">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1344" data-mp="pakurva11coi">
            <h2 className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-1345" data-mp="pcxjcraailaj">
              Kies het pakket dat bij je werk past
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-1346" data-mp="fk9ow36s8tez">
              Vier pakketten met e-mail op je eigen domein. Bij elk Business-pakket zit Kodee AI inbegrepen. Tik op een pakket om de prijs, opslag en features te zien.
            </p>
          </div>

          {/* Toggle */}
          <div className="max-w-4xl mx-auto mb-10" data-id="element-1347" data-mp="28a2a7zx6b4tz">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-2 bg-[#F7F8FA] rounded-2xl" data-id="element-1348" data-mp="28atw2j0yxo72">
              {emailPackages.map(pkg => {
              const isActive = pkg.id === selectedPackage;
              return <button key={pkg.id} onClick={() => setSelectedPackage(pkg.id)} className={`flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${isActive ? 'bg-white text-[#007AFF] shadow-md' : 'bg-transparent text-[#6B7280] hover:text-[#111827]'}`} data-id="element-1349" data-mp="1uidxbuf3ocby">
                    {pkg.icon}
                    <span data-id="element-1350" data-mp="16t2um7e6w7v6">{pkg.shortName}</span>
                    {pkg.badge && isActive && <span className="ml-1 hidden lg:inline-flex items-center gap-1 bg-[#007AFF] text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" data-id="element-1351" data-mp="11sx815ms1bjy">
                        <StarIcon className="h-3 w-3" data-id="element-1352" data-mp="wocywj9dpzze" />
                        {pkg.badge}
                      </span>}
                  </button>;
            })}
            </div>
          </div>

          {/* Pakket card (dynamic) */}
          <AnimatePresence mode="wait" data-id="element-1353" data-mp="16giqvszmshel">
            <motion.div key={activePackage.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.25
          }} className="bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-8 md:p-12 max-w-5xl mx-auto shadow-sm border border-gray-100" data-id="element-1354" data-mp="1agj3d8366ij0">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-1355" data-mp="3m6bxtvoinrmg">
                {/* Prijs blok */}
                <div className="md:col-span-5" data-id="element-1356" data-mp="3r5g6ml7j3nc9">
                  <div className="bg-white rounded-2xl p-6 md:p-8 shadow-md" data-id="element-1357" data-mp="1lgcrl19371sc">
                    <div className="inline-flex items-center gap-2 bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-semibold px-3 py-1 rounded-full mb-4" data-id="element-1358" data-mp="1lih5mc0p9h6c">
                      {activePackage.icon}
                      <span data-id="element-1359" data-mp="1ueygde55jd1k">{activePackage.name}</span>
                    </div>
                    <p className="text-[#6B7280] text-sm mb-4 leading-relaxed" data-id="element-1360" data-mp="1pihme3b9u4gk">
                      {activePackage.tagline}
                    </p>
                    <div className="flex items-baseline gap-2 mb-2" data-id="element-1361" data-mp="xr1vvy6lfxl2">
                      <span className="text-5xl font-bold text-[#111827]" data-id="element-1362" data-mp="xoyz8vaxr67d">
                        {activePackage.price}
                      </span>
                    </div>
                    <p className="text-[#6B7280] text-sm mb-4" data-id="element-1363" data-mp="16px7xqwbk1wa">
                      {activePackage.unit}
                    </p>
                    <div className="border-t border-gray-200 pt-4 mb-6" data-id="element-1364" data-mp="izjdxuu6bsz2">
                      <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1365" data-mp="e2j4rb212a3q">
                        Opslag
                      </p>
                      <p className="text-[#111827] font-semibold" data-id="element-1366" data-mp="2uhefj1tanins">
                        {activePackage.storage} per mailbox
                      </p>
                    </div>
                    <a href="/contact" className="inline-flex items-center justify-center w-full bg-[#007AFF] text-white font-semibold py-3 px-6 rounded-lg shadow-md" data-id="element-1367" data-mp="2loklfy96uepv">
                      Vraag {activePackage.shortName} aan
                      <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1368" data-mp="3ddec6px7vadp" />
                    </a>
                    <p className="text-xs text-[#6B7280] text-center mt-3" data-id="element-1369" data-mp="2zi25rl9p7vgy">
                      Jaarlijks vastgelegd, prijzen excl. BTW. Setup en oplevering door Noord Digital.
                    </p>
                  </div>
                </div>

                {/* Inhoud blok */}
                <div className="md:col-span-7" data-id="element-1370" data-mp="1t1gn2c4loyn2">
                  <h3 className="font-bold text-[#111827] text-2xl mb-3" data-id="element-1371" data-mp="18xj51bb2vls1">
                    Wat zit erin?
                  </h3>
                  <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-1372" data-mp="14x8rndkyht35">
                    {activePackage.description}
                  </p>
                  <ul className="space-y-3 mb-6" data-id="element-1373" data-mp="1034g0h0qwwvq">
                    {activePackage.features.map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-1374" data-mp="q71fffljl6aw" data-mpi={i}>
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1375" data-mp="q56s5umst23n" />
                        <span className="text-[#6B7280] leading-relaxed" data-id="element-1376" data-mp="laazqtdngcq0">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  {/* Kodee AI uitleg bij alle Business tiers */}
                  {activePackage.hasKodee && <div className="bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-2xl p-6 text-white shadow-lg" data-id="element-1377" data-mp="26lswwxh5gt5f">
                      <div className="flex items-start gap-4" data-id="element-1378" data-mp="2e5u3u72tczne">
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1379" data-mp="2e37tjzojg61h">
                          <BotIcon className="h-6 w-6 text-white" data-id="element-1380" data-mp="2nulri30ucxsi" />
                        </div>
                        <div data-id="element-1381" data-mp="2su836810u5z7">
                          <h4 className="font-bold text-lg mb-2" data-id="element-1382" data-mp="2stf6jzvs0wus">
                            Kodee AI inbegrepen
                          </h4>
                          <p className="text-white text-opacity-90 leading-relaxed text-sm mb-3" data-id="element-1383" data-mp="20516lel4nqgl">
                            Bij alle Business-pakketten krijg je Kodee, de AI assistent die naast je werkt in je mailbox. Geen extra abonnement, geen losse koppeling.
                          </p>
                          <ul className="space-y-2 text-sm text-white text-opacity-90" data-id="element-1384" data-mp="3gjj881pemmj5">
                            <li className="flex items-start gap-2" data-id="element-1385" data-mp="3glvl40gb26pc">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1386" data-mp="3kjjm9l8lcvpo" />
                              <span data-id="element-1387" data-mp="ai20d9n8rmhi">Slimme AI-antwoorden en berichtconcepten in jouw eigen toon</span>
                            </li>
                            <li className="flex items-start gap-2" data-id="element-1388" data-mp="5gcoxsw3x6sw">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1389" data-mp="k8mzcy6tut0y" />
                              <span data-id="element-1390" data-mp="ffli4jj5ehe7">Directe AI-samenvattingen van lange threads</span>
                            </li>
                            <li className="flex items-start gap-2" data-id="element-1391" data-mp="t5xbelrgym6x">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1392" data-mp="o7nfg8xx7hk9" />
                              <span data-id="element-1393" data-mp="1367qkbx2e3oq">AI-gestuurde e-mailzoekfunctie op onderwerp in plaats van trefwoord</span>
                            </li>
                            <li className="flex items-start gap-2" data-id="element-1394" data-mp="y7mkvwz1mrsw">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1395" data-mp="34ycbh3g5c0py" />
                              <span data-id="element-1396" data-mp="2zyznughgapmb">Agentische e-mail (bèta): laat Kodee zelfstandig acties oppakken</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Vergelijkings-strook */}
          <div className="max-w-5xl mx-auto mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1397" data-mp="1aop5ixnpmap3">
            {emailPackages.map(pkg => {
            const isActive = pkg.id === selectedPackage;
            return <button key={pkg.id} onClick={() => setSelectedPackage(pkg.id)} className={`text-left p-5 rounded-xl border-2 transition-all duration-200 ${isActive ? 'border-[#007AFF] bg-white shadow-md' : 'border-transparent bg-[#F7F8FA] hover:border-gray-200'}`} data-id="element-1398" data-mp="10s2luc74dpon">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1399" data-mp="2m2je6tk76fus">
                    <div className="w-8 h-8 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF]" data-id="element-1400" data-mp="3po0eqfi05d9f">
                      {pkg.icon}
                    </div>
                    <span className="font-semibold text-[#111827] text-sm leading-tight" data-id="element-1401" data-mp="3gp56yyzb7v40">
                      {pkg.shortName}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-[#111827]" data-id="element-1402" data-mp="c9dwf5hqxz81">
                    {pkg.price}
                  </p>
                  <p className="text-xs text-[#6B7280] mb-2" data-id="element-1403" data-mp="h7xjj982th1z">{pkg.unit}</p>
                  <p className="text-xs text-[#007AFF] font-semibold" data-id="element-1404" data-mp="39qri9pt6ba8">
                    {pkg.storage} opslag
                  </p>
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* 3-mailbox advies */}
      <div id="mailbox-advies" className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-20 md:py-24" data-id="element-1405" data-mp="2o1j6hzldp6ld">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1406" data-mp="2sw6mtb68auov">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1407" data-mp="2kfpjftph6hit">
            <span className="inline-block bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full mb-4" data-id="element-1408" data-mp="2gevtyvwdwunt">
              Ons advies
            </span>
            <h2 className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-1409" data-mp="2phn8j4kk46jo">
              Drie mailboxen, drie functies, één strak systeem
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-1410" data-mp="1wry7wsbre8il">
              We adviseren elke adviespraktijk om de mail te splitsen over drie functies. Het houdt je inbox overzichtelijk en zorgt dat de juiste mensen de juiste mail krijgen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-1411" data-mp="26o17prj51v2d">
            {/* Info */}
            <div className="bg-white rounded-2xl p-8 shadow-md" data-id="element-1412" data-mp="1iy6uv9ytk6i4">
              <div className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-1413" data-mp="1e0x8622te8xz">
                <BriefcaseIcon className="h-7 w-7" data-id="element-1414" data-mp="3j2swk2hbdxrb" />
              </div>
              <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1415" data-mp="3j0yb8n24s3mp">
                Algemene communicatie
              </p>
              <h3 className="font-bold text-[#111827] text-xl mb-3 font-mono" data-id="element-1416" data-mp="3ruuv1v7tp3yy">
                info@<span className="text-[#007AFF]" data-id="element-1417" data-mp="3rwxl2f8z6x3o">jouwbedrijf</span>.nl
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-1418" data-mp="2v6wzep3wkwql">
                Voor de grote communicatie met klanten, partners en het algemene contact. Dit is het adres dat op je website, visitekaartje en briefpapier staat.
              </p>
            </div>

            {/* Administratie */}
            <div className="bg-white rounded-2xl p-8 shadow-md" data-id="element-1419" data-mp="392ikil70mwdq">
              <div className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-1420" data-mp="jehv1v870w1r">
                <FileTextIcon className="h-7 w-7" data-id="element-1421" data-mp="og779e75jfrf" />
              </div>
              <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1422" data-mp="yht32h5j5e2c">
                Zakelijke accounts en software
              </p>
              <h3 className="font-bold text-[#111827] text-xl mb-3 font-mono" data-id="element-1423" data-mp="os9hv4uch84b">
                administratie@<span className="text-[#007AFF]" data-id="element-1424" data-mp="kqwed1eq5h0k">jouwbedrijf</span>.nl
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-1425" data-mp="kp3cdzf23kyw">
                Voor je Google-account, software-aanmeldingen en alle andere services waar een zakelijk account makkelijker werkt. Geen verwarring met je dagelijkse mail.
              </p>
            </div>

            {/* Facturen */}
            <div className="bg-white rounded-2xl p-8 shadow-md" data-id="element-1426" data-mp="1s1wvknvm2gp">
              <div className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-1427" data-mp="1tv4b26bcqpr">
                <ReceiptIcon className="h-7 w-7" data-id="element-1428" data-mp="1w633wstkxvrv" />
              </div>
              <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1429" data-mp="1w6ut9lwl2x9p">
                Boekhouding en facturatie
              </p>
              <h3 className="font-bold text-[#111827] text-xl mb-3 font-mono" data-id="element-1430" data-mp="288w1hwk4o2bo">
                facturen@<span className="text-[#007AFF]" data-id="element-1431" data-mp="1u8v10i7rywwq">jouwbedrijf</span>.nl
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-1432" data-mp="1ya6i1zcl29tb">
                Voor alle inkomende en uitgaande facturen. Stuur dit adres door naar je boekhouder of administratiekantoor, en jouw eigen inbox blijft schoon.
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto mt-10 bg-white rounded-2xl p-6 text-center shadow-sm" data-id="element-1433" data-mp="1pbl3efa8fext">
            <p className="text-[#6B7280] leading-relaxed" data-id="element-1434" data-mp="1aiiy61dqnz0j">
              Drie mailboxen, allemaal in hetzelfde pakket. Bij <span className="font-semibold text-[#111827]" data-id="element-1435" data-mp="1fhcc1uwxts18">{activePackage.shortName}</span> betaal je voor drie mailboxen <span className="font-bold text-[#007AFF]" data-id="element-1436" data-mp="16j0oy2va5cdj">{(parseFloat(activePackage.price.replace('€', '').replace(',', '.')) * 3).toFixed(2).replace('.', ',')} euro per maand</span>, jaarlijks vastgelegd.
            </p>
          </div>
        </div>
      </div>

      {/* Email marketing via Hostinger Reach */}
      <div className="w-full bg-white py-20 md:py-24" data-id="element-1437" data-mp="vxn9zs51ou8h">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1438" data-mp="vz83rfjjc95a">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1439" data-mp="rviiayy5q7oh">
            <span className="inline-block bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full mb-4" data-id="element-1440" data-mp="15vt6fu83185y">
              Email marketing
            </span>
            <h2 className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-1441" data-mp="1fme49ffxm8nt">
              Nieuwsbrieven en campagnes, schaalt met je bedrijf mee
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-1442" data-mp="1ap5lbcmym0li">
              Naast dagelijks mailen kunnen we ook je nieuwsbrieven, campagnes en geautomatiseerde follow-ups voor je inrichten. Kies hieronder de schaal die bij jouw lijst past.
            </p>
          </div>

          {/* Reach toggle */}
          <div className="max-w-5xl mx-auto mb-8" data-id="element-1443" data-mp="m962e6caoo6u">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 p-2 bg-[#F7F8FA] rounded-2xl" data-id="element-1444" data-mp="m9xzjlljitz8">
              {reachTiers.map(tier => {
              const isActive = tier.id === selectedReach;
              return <button key={tier.id} onClick={() => setSelectedReach(tier.id)} className={`flex flex-col items-center justify-center px-2 py-3 rounded-xl font-semibold text-xs transition-all duration-200 ${isActive ? 'bg-white text-[#007AFF] shadow-md' : 'bg-transparent text-[#6B7280] hover:text-[#111827]'}`} data-id="element-1445" data-mp="100jkdfuk1dzr">
                    <span className="text-[10px] uppercase tracking-wide opacity-70 mb-1" data-id="element-1446" data-mp="3bs6mwh62qkpv">
                      Tot
                    </span>
                    <span className="text-sm font-bold" data-id="element-1447" data-mp="388fytktawz46">
                      {tier.contacts.split(' ')[0]}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Reach detail */}
          <AnimatePresence mode="wait" data-id="element-1448" data-mp="2yg0guni08aja">
            <motion.div key={activeReach.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.25
          }} className="bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-8 md:p-12 max-w-5xl mx-auto shadow-sm border border-gray-100" data-id="element-1449" data-mp="2uhkpy8isyi0l">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-1450" data-mp="3d82gc6g5x1is">
                <div className="md:col-span-5" data-id="element-1451" data-mp="26rqdhs2eavgv">
                  <div className="bg-white rounded-2xl p-6 md:p-8 shadow-md" data-id="element-1452" data-mp="26sia967qaz8q">
                    <div className="inline-flex items-center gap-2 bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-semibold px-3 py-1 rounded-full mb-4" data-id="element-1453" data-mp="21rb2361oby1j">
                      <SendIcon className="h-4 w-4" data-id="element-1454" data-mp="9qb4ap50iee5" />
                      <span data-id="element-1455" data-mp="9rvvq5w15h7s">{activeReach.range}</span>
                    </div>
                    <div className="flex items-baseline gap-2 mb-2" data-id="element-1456" data-mp="3rxh9xntrc1xj">
                      <span className="text-5xl font-bold text-[#111827]" data-id="element-1457" data-mp="pcu1p3zdan4">
                        {activeReach.price}
                      </span>
                    </div>
                    <p className="text-[#6B7280] text-sm mb-6" data-id="element-1458" data-mp="ohtfm9mxfkr6">
                      per maand, jaarlijks vastgelegd
                    </p>
                    <div className="space-y-3 border-t border-gray-200 pt-4 mb-6" data-id="element-1459" data-mp="eombes3xaaso">
                      <div className="flex items-center gap-3" data-id="element-1460" data-mp="jo78sy3g2b3a">
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1461" data-mp="jme2jjwqtsvt" />
                        <span className="text-sm text-[#6B7280]" data-id="element-1462" data-mp="17c8fe1h2bhg2">
                          {activeReach.contacts}
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1463" data-mp="2wu5isngszo02">
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1464" data-mp="2rwvpgwqams6y" />
                        <span className="text-sm text-[#6B7280]" data-id="element-1465" data-mp="35nimmkgeoa0x">
                          {activeReach.emails}
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1466" data-mp="31o9rbb5pw439">
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1467" data-mp="2dxdw0421yqr9" />
                        <span className="text-sm text-[#6B7280]" data-id="element-1468" data-mp="28yjagucpao3g">
                          {activeReach.aiCredits}
                        </span>
                      </div>
                    </div>
                    <a href="/contact" className="inline-flex items-center justify-center w-full bg-[#007AFF] text-white font-semibold py-3 px-6 rounded-lg shadow-md" data-id="element-1469" data-mp="2hx4vcw5naf7e">
                      Vraag email marketing aan
                      <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1470" data-mp="bus2zrggsfel" />
                    </a>
                  </div>
                </div>

                <div className="md:col-span-7" data-id="element-1471" data-mp="7rdba3j5yalb">
                  <h3 className="font-bold text-[#111827] text-2xl mb-3" data-id="element-1472" data-mp="2tlhf3yykeeh">
                    Wat zit erin?
                  </h3>
                  <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-1473" data-mp="3u16eljr9gd9t">
                    Alles wat je nodig hebt om campagnes te draaien die meegroeien met je bedrijf. Sjablonen op je merk gezet, automatisering vanuit je website, en rapportage waar je iets aan hebt.
                  </p>
                  <ul className="space-y-3 mb-6" data-id="element-1474" data-mp="lrxw158n2ghg">
                    <li className="flex items-start gap-3" data-id="element-1475" data-mp="lqcxz5n48feu">
                      <SendIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1476" data-mp="gstk46uby4nf" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1477" data-mp="1ea21jalgjj7o">
                        Nieuwsbrieven plannen, met tracking per open en klik
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1478" data-mp="1guhwn96urmvi">
                      <SparklesIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1479" data-mp="1lsjcll8zf62r" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1480" data-mp="1qrm9giet65zn">
                        AI e-mail templates en onderwerpregel-generator
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1481" data-mp="1zsap1hjuoeen">
                      <BotIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1482" data-mp="1zrrhmurtt8ev" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1483" data-mp="24q2rh5i4fix3">
                        Welkomst- en opvolg-e-mails over dagen of weken
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1484" data-mp="2efvqz84zo7kz">
                      <UsersIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1485" data-mp="2jgjqyjb73yrc" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1486" data-mp="2nhv9y5zkdlqk">
                        Onbeperkte doelgroepsegmenten, handmatig of met AI
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1487" data-mp="je0s5beybv4a">
                      <TrendingUpIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1488" data-mp="jflhmmmesnva" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1489" data-mp="x7026xvysx4g">
                        Volledig eigen branding, geen Reach-logo onder je mail
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1490" data-mp="171s1c6573s1c">
                      <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1491" data-mp="125bergdn9lqd" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1492" data-mp="1g4ku6du10bta">
                        Sjablonen die we voor jou op merk zetten, plus synchronisatie met je website
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Norden email security */}
      <div className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-20 md:py-24" data-id="element-1493" data-mp="33pvxrzrwzfzq">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1494" data-mp="37s916hj6v1tc">
          <div className="max-w-4xl mx-auto" data-id="element-1495" data-mp="2u0ce4rwdni3t">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm" data-id="element-1496" data-mp="2tzjhijr4u8ze">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-1497" data-mp="2yymah5tu8or0">
                <div className="text-center md:text-left" data-id="element-1498" data-mp="2k2epi3alvjao">
                  <div className="inline-block" data-id="element-1499" data-mp="2k0txakbrkcfz">
                    <div className="w-48 h-48 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full flex items-center justify-center p-4" data-id="element-1500" data-mp="2p1rf2vsv34gy">
                      <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden bewaakt zakelijke e-mail hosting" className="w-full h-full object-cover rounded-full" data-id="element-1501" data-mp="2b3b7z3r37bya" />
                    </div>
                  </div>
                </div>
                <div data-id="element-1502" data-mp="2ab08hu00selv">
                  <h2 className="font-bold text-[#111827] text-3xl mb-4" data-id="element-1503" data-mp="2aa8prjriubd0">
                    Hoe we je oplevering aanpakken
                  </h2>
                  <p className="text-[#6B7280] text-lg leading-relaxed mb-6" data-id="element-1504" data-mp="20ddud9qjor2y">
                    Wij doen de DNS, de SPF, DKIM en DMARC, de domeinverificatie en de oplevering in Outlook of Apple Mail. Jij krijgt een werkende mailbox op de afgesproken dag, met je oude mail meeverhuisd als je dat wilt.
                  </p>
                  <div className="space-y-3" data-id="element-1505" data-mp="1mlz0u8z0h7hn">
                    <div className="flex items-center gap-3" data-id="element-1506" data-mp="1hnnq7w0w6syd">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1507" data-mp="1hnxffhe44w30">
                        <CheckCircleIcon className="h-4 w-4 text-white" data-id="element-1508" data-mp="1cmq63dwbnou8" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-1509" data-mp="18osgi9yn30qb">
                        DNS, SPF, DKIM en DMARC standaard ingericht
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-1510" data-mp="3fayl9ld4jnmf">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1511" data-mp="3k9jhzat63p64">
                        <CheckCircleIcon className="h-4 w-4 text-white" data-id="element-1512" data-mp="3ocfx2rji135b" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-1513" data-mp="3tahfda93oyfq">
                        Bestaande mail meeverhuisd waar mogelijk
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-1514" data-mp="30lk6fxqs4dzn">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1515" data-mp="35lezo7y11fnv">
                        <CheckCircleIcon className="h-4 w-4 text-white" data-id="element-1516" data-mp="3acoq3aregn0t" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-1517" data-mp="2cxapb94igeeu">
                        Persoonlijke uitleg over je mailapp na oplevering
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA-rij */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#005FCC] py-16 md:py-20" data-id="element-1518" data-mp="3g6vorr6tcwvu">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] text-center" data-id="element-1519" data-mp="3ty0i6bjl8sxq">
          <h2 className="font-bold text-white text-3xl md:text-4xl mb-4" data-id="element-1520" data-mp="3pyj8f5x4p9mr">
            Combineer je e-mail met de rest
          </h2>
          <p className="text-white text-opacity-90 text-lg leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-1521" data-mp="3285q474cip5d">
            Email, hosting en website bij één partij betekent korte lijnen, nét als bij de rest van je dienstverlening.
          </p>
          <div className="flex flex-wrap gap-4 justify-center" data-id="element-1522" data-mp="369qzhiho8lat">
            <a href="/hosting" className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-md" data-id="element-1523" data-mp="36866vyex3cfl">
              Bekijk hosting (€34,99 per maand)
              <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1524" data-mp="u0acozh8yff3" />
            </a>
            <a href="/websites" className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg" data-id="element-1525" data-mp="u2dbo90dnivy">
              Website laten maken (vanaf €499)
              <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1526" data-mp="p0nx4jdy4n34" />
            </a>
          </div>
        </div>
      </div>

      <Footer data-id="element-1527" data-mp="1cqiar363afof" />
    </div>;
};